/**
 * Network Interceptor
 *
 * Monkey-patches fetch() and XMLHttpRequest to observe API calls.
 * Captures request/response **shapes** (key names + types) — never actual values.
 * Only intercepts same-origin requests to avoid capturing third-party analytics.
 */

// ─── Types ────────────────────────────────────────────────────────────────

export interface CapturedApiCall {
  method: string;
  urlPath: string;          // pathname only — no query params, no domain
  status: number;
  responseShape?: string;   // e.g. "{ data: [{ id, name, email }], total }"
  requestShape?: string;    // for POST/PUT body shape
  triggeredBy: string;      // route that made this call
  durationMs: number;
}

// ─── Shape Extraction ─────────────────────────────────────────────────────

/**
 * Extract the structural shape of a JSON value.
 * Strips all actual data — only keeps key names and types.
 *
 * Example: { data: [{ id: 1, name: "Alice" }], total: 42 }
 *       → "{ data: [{ id, name }], total }"
 */
function extractShape(value: unknown, depth = 0): string {
  if (depth > 4) return '...';

  if (value === null || value === undefined) return 'null';
  if (typeof value === 'string') return 'string';
  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';

  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    // Only describe the first element's shape
    return `[${extractShape(value[0], depth + 1)}]`;
  }

  if (typeof value === 'object') {
    const keys = Object.keys(value as Record<string, unknown>).slice(0, 20);
    if (keys.length === 0) return '{}';

    const entries = keys.map((key) => {
      const child = (value as Record<string, unknown>)[key];
      const childShape = extractShape(child, depth + 1);
      // For primitive leaf values, just show the key name
      if (['string', 'number', 'boolean', 'null'].includes(childShape)) {
        return key;
      }
      return `${key}: ${childShape}`;
    });

    return `{ ${entries.join(', ')} }`;
  }

  return 'unknown';
}

/**
 * Strip query params and hash from a URL path.
 */
function toCleanPath(url: string): string {
  try {
    const parsed = new URL(url, location.origin);
    return parsed.pathname;
  } catch {
    return url.split('?')[0].split('#')[0];
  }
}

/**
 * Determine if a URL is same-origin (or a relative API path).
 */
function isSameOrigin(url: string): boolean {
  // Relative URLs are same-origin
  if (url.startsWith('/')) return true;
  try {
    const parsed = new URL(url, location.origin);
    return parsed.origin === location.origin;
  } catch {
    return false;
  }
}

/**
 * Determine if a URL looks like an API call (not a static asset).
 */
function isApiCall(path: string): boolean {
  // Skip static assets
  const staticExts = ['.js', '.css', '.png', '.jpg', '.jpeg', '.gif', '.svg', '.ico', '.woff', '.woff2', '.ttf', '.eot', '.map'];
  if (staticExts.some((ext) => path.endsWith(ext))) return false;

  // Skip HMR / webpack dev server
  if (path.includes('__webpack') || path.includes('_next/static') || path.includes('hot-update')) return false;

  // Likely API if it contains /api/ or returns JSON (checked later)
  return true;
}

// ─── Interceptor Class ────────────────────────────────────────────────────

export class NetworkInterceptor {
  private calls: CapturedApiCall[] = [];
  private originalFetch: typeof fetch | null = null;
  private originalXhrOpen: typeof XMLHttpRequest.prototype.open | null = null;
  private originalXhrSend: typeof XMLHttpRequest.prototype.send | null = null;
  private started = false;

  start(): void {
    if (this.started) return;
    this.started = true;
    this.interceptFetch();
    this.interceptXHR();
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;

    // Restore originals
    if (this.originalFetch) {
      window.fetch = this.originalFetch;
      this.originalFetch = null;
    }
    if (this.originalXhrOpen) {
      XMLHttpRequest.prototype.open = this.originalXhrOpen;
      this.originalXhrOpen = null;
    }
    if (this.originalXhrSend) {
      XMLHttpRequest.prototype.send = this.originalXhrSend;
      this.originalXhrSend = null;
    }
  }

  /**
   * Drain and return all captured calls since last drain.
   */
  drain(): CapturedApiCall[] {
    const result = this.calls;
    this.calls = [];
    return result;
  }

  // ─── Fetch Interception ───────────────────────────────────────────────

  private interceptFetch(): void {
    const self = this;
    this.originalFetch = window.fetch;
    const origFetch = this.originalFetch;

    window.fetch = async function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      const method = (init?.method ?? 'GET').toUpperCase();

      if (!isSameOrigin(url) || !isApiCall(toCleanPath(url))) {
        return origFetch.call(window, input, init);
      }

      const startTime = performance.now();
      let requestShape: string | undefined;

      // Capture request body shape
      if (init?.body && typeof init.body === 'string') {
        try {
          const parsed = JSON.parse(init.body);
          requestShape = extractShape(parsed);
        } catch { /* not JSON */ }
      }

      try {
        const response = await origFetch.call(window, input, init);
        const durationMs = Math.round(performance.now() - startTime);

        // Clone response to read body without consuming it
        const contentType = response.headers.get('content-type') ?? '';
        let responseShape: string | undefined;

        if (contentType.includes('application/json')) {
          try {
            const clone = response.clone();
            const json = await clone.json();
            responseShape = extractShape(json);
          } catch { /* failed to parse */ }
        }

        self.recordCall({
          method,
          urlPath: toCleanPath(url),
          status: response.status,
          responseShape,
          requestShape,
          triggeredBy: location.pathname,
          durationMs,
        });

        return response;
      } catch (err) {
        self.recordCall({
          method,
          urlPath: toCleanPath(url),
          status: 0,
          requestShape,
          triggeredBy: location.pathname,
          durationMs: Math.round(performance.now() - startTime),
        });
        throw err;
      }
    };
  }

  // ─── XHR Interception ─────────────────────────────────────────────────

  private interceptXHR(): void {
    const self = this;
    this.originalXhrOpen = XMLHttpRequest.prototype.open;
    this.originalXhrSend = XMLHttpRequest.prototype.send;
    const origOpen = this.originalXhrOpen;
    const origSend = this.originalXhrSend;

    XMLHttpRequest.prototype.open = function (method: string, url: string | URL, ...rest: unknown[]) {
      const urlStr = typeof url === 'string' ? url : url.href;
      (this as XMLHttpRequest & { _da_method: string; _da_url: string })._da_method = method.toUpperCase();
      (this as XMLHttpRequest & { _da_url: string })._da_url = urlStr;
      return (origOpen as Function).call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
      const xhr = this as XMLHttpRequest & { _da_method: string; _da_url: string };
      const url = xhr._da_url;
      const method = xhr._da_method ?? 'GET';

      if (!url || !isSameOrigin(url) || !isApiCall(toCleanPath(url))) {
        return origSend.call(this, body);
      }

      const startTime = performance.now();
      let requestShape: string | undefined;

      if (body && typeof body === 'string') {
        try {
          requestShape = extractShape(JSON.parse(body));
        } catch { /* not JSON */ }
      }

      xhr.addEventListener('load', () => {
        const durationMs = Math.round(performance.now() - startTime);
        const contentType = xhr.getResponseHeader('content-type') ?? '';
        let responseShape: string | undefined;

        if (contentType.includes('application/json') && xhr.responseText) {
          try {
            responseShape = extractShape(JSON.parse(xhr.responseText));
          } catch { /* failed to parse */ }
        }

        self.recordCall({
          method,
          urlPath: toCleanPath(url),
          status: xhr.status,
          responseShape,
          requestShape,
          triggeredBy: location.pathname,
          durationMs,
        });
      });

      return origSend.call(this, body);
    };
  }

  // ─── Recording ────────────────────────────────────────────────────────

  private recordCall(call: CapturedApiCall): void {
    // Cap at 100 calls per flush cycle to prevent memory issues
    if (this.calls.length < 100) {
      this.calls.push(call);
    }
  }
}
